<script setup lang="ts">
/**
 * A target, and how far along it is.
 *
 * The bar is derived from the three figures beside it rather than typed, so it
 * cannot disagree with them. That is the whole reason this is a section kind
 * rather than a sentence somebody writes: a progress figure kept in step by
 * hand eventually says 80% next to numbers that are 30% of the way there, and
 * nobody reading it would know which to believe.
 *
 * When a figure is missing the bar is not drawn at all. A bar at zero asserts
 * that no progress has been made, which is a different statement from having
 * nothing to report yet.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { progressOf } from '@/domain/progress'
import type { TargetSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: TargetSection }>()

const label = computed(() => props.section.label.trim() || props.section.title)

const progress = computed(() =>
  progressOf(props.section.baseValue, props.section.targetValue, props.section.currentValue),
)

const percent = computed(() => (progress.value ? Math.round(progress.value.fraction * 100) : null))
</script>

<template>
  <div class="target">
    <p class="target__label">{{ label || t('canvas.defaults.target') }}</p>

    <div class="target__figures">
      <div class="target__figure">
        <span class="target__caption">{{ t('inspector.target.base') }}</span>
        <span class="target__value">{{ section.baseValue || '—' }}</span>
        <span class="target__year">{{ section.baseYear }}</span>
      </div>

      <div class="target__figure target__figure--now">
        <span class="target__caption">{{ t('inspector.target.current') }}</span>
        <span class="target__value">{{ section.currentValue || '—' }}</span>
        <span v-if="section.unit" class="target__year">{{ section.unit }}</span>
      </div>

      <div class="target__figure target__figure--goal">
        <span class="target__caption">{{ t('inspector.target.goal') }}</span>
        <span class="target__value">{{ section.targetValue || '—' }}</span>
        <span class="target__year">{{ section.targetYear }}</span>
      </div>
    </div>

    <!-- Only where all three figures can be read. -->
    <div v-if="progress && percent !== null" class="target__track">
      <div
        class="target__fill"
        :class="{ 'target__fill--done': progress.reached }"
        :style="{ width: `${percent}%` }"
      />
      <span class="target__percent">
        {{ progress.regressing ? t('inspector.target.regressing') : `${percent}%` }}
      </span>
    </div>

    <p v-if="section.note" class="target__note">{{ section.note }}</p>
  </div>
</template>

<style scoped>
.target {
  margin: 3mm 0 4mm;
  padding: 3mm 3.5mm;
  border: 1px solid var(--paper-rule);
  border-radius: 1.5mm;
}

.target__label {
  margin: 0 0 2.5mm;
  font-size: 10.5pt;
  font-weight: 600;
}

.target__figures {
  display: flex;
  align-items: flex-end;
  gap: 6mm;
}

.target__figure {
  display: flex;
  flex-direction: column;
  gap: 0.6mm;
}

/* The one that matters most, set largest. A reader wants to know where things
   stand before they want to know where they started. */
.target__figure--now {
  flex: 1;
}

.target__figure--goal {
  text-align: right;
}

.target__caption {
  color: var(--paper-ink-soft);
  font-size: 7.5pt;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.target__value {
  font-size: 12pt;
  font-variant-numeric: tabular-nums lining-nums;
}

.target__figure--now .target__value {
  font-size: 17pt;
  font-weight: 600;
  color: var(--page-accent);
}

.target__year {
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
}

.target__track {
  position: relative;
  height: 4mm;
  margin-top: 3mm;
  border-radius: 999px;
  background: var(--paper-rule);
  overflow: hidden;
}

.target__fill {
  height: 100%;
  border-radius: 999px;
  background: var(--page-accent);
}

.target__fill--done {
  /* Reached, and said so by filling rather than by a second colour: a target
     met is not a different kind of thing, it is a full bar. */
  opacity: 1;
}

.target__percent {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 7.5pt;
  font-weight: 600;
  /* Legible over both the filled and the unfilled part, which is what a bar
     with the figure inside it has to be. */
  mix-blend-mode: difference;
  color: #ffffff;
}

.target__note {
  margin: 2.5mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
  line-height: 1.5;
  text-align: left;
}

@media print {
  .target__fill,
  .target__track {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
